import React from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard, Bot, ListChecks, Sparkles, Network, KeyRound, BarChart3,
  BrainCircuit, Calendar,
  Settings as SettingsIcon, ShieldCheck, Search, ChevronsLeft, ChevronsRight,
  LogOut, CreditCard, Terminal, PlusCircle, Server, Smartphone, BookOpen,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAuthStore } from '@/stores/auth';
import { useUIStore } from '@/stores/ui';
import { Logo } from '@/components/Logo';
import { CommandPalette } from '@/components/CommandPalette';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger, DropdownMenuLabel,
} from '@/components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Button } from '@/components/ui/button';

const NAV = [
  { section: 'Operate', items: [
    { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { to: '/master', label: 'Master Agent', icon: BrainCircuit },
    { to: '/agents', label: 'Agents', icon: Bot },
    { to: '/tasks', label: 'Tasks', icon: ListChecks },
    { to: '/schedules', label: 'Schedules', icon: Calendar },
    { to: '/templates', label: 'Templates', icon: Sparkles },
  ]},
  { section: 'Infrastructure', items: [
    { to: '/workers', label: 'Workers', icon: Server },
    { to: '/proxies', label: 'Proxies', icon: Network },
    { to: '/identities', label: 'Identities', icon: Smartphone },
    { to: '/docs', label: 'Docs', icon: BookOpen },
    { to: '/credentials', label: 'Credentials', icon: ShieldCheck },
  ]},
  { section: 'Organization', items: [
    { to: '/analytics', label: 'Analytics', icon: BarChart3 },
    { to: '/api-keys', label: 'API Keys', icon: KeyRound },
    { to: '/billing', label: 'Billing', icon: CreditCard },
    { to: '/settings', label: 'Settings', icon: SettingsIcon },
  ]},
];

export default function AppShell() {
  const { user, clear } = useAuthStore();
  const { sidebarCollapsed, toggleSidebar, setCommandOpen } = useUIStore();
  const nav = useNavigate();

  React.useEffect(() => {
    const onKey = (e) => {
      const isTyping = /input|textarea|select/i.test(e.target?.tagName || '');
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setCommandOpen(true);
      } else if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'b') {
        e.preventDefault();
        toggleSidebar();
      } else if (e.key === '/' && !isTyping) {
        e.preventDefault();
        setCommandOpen(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [setCommandOpen, toggleSidebar]);

  const handleLogout = () => { clear(); nav('/login'); };

  const sidebarW = sidebarCollapsed ? 'w-[72px]' : 'w-[264px]';

  return (
    <TooltipProvider delayDuration={200}>
      <div className="min-h-screen bg-[hsl(var(--background))] text-[hsl(var(--foreground))]">
        <div className="flex">
          {/* Sidebar */}
          <aside
            className={cn('fixed inset-y-0 left-0 z-30 hidden lg:flex flex-col bg-[hsl(var(--surface-1))] border-r border-white/5 transition-[width] duration-200', sidebarW)}
            data-testid="app-sidebar"
          >
            <div className="flex h-12 items-center justify-between px-3 border-b border-white/5">
              <div className="flex items-center gap-2 min-w-0">
                <Logo withWord={!sidebarCollapsed} />
              </div>
              <button
                onClick={toggleSidebar}
                className="rounded-md p-1.5 text-[hsl(var(--text-3))] hover:bg-[hsl(var(--surface-2))] hover:text-[hsl(var(--text-1))] transition-colors"
                aria-label="Toggle sidebar"
                data-testid="sidebar-toggle-button"
              >
                {sidebarCollapsed ? <ChevronsRight className="h-4 w-4" /> : <ChevronsLeft className="h-4 w-4" />}
              </button>
            </div>

            <nav className="flex-1 overflow-y-auto px-2 py-3" data-testid="app-sidebar-nav">
              {NAV.map((group) => (
                <div key={group.section} className="mb-4">
                  {!sidebarCollapsed && (
                    <div className="px-2.5 pb-1.5 text-[10px] uppercase tracking-[0.14em] text-[hsl(var(--text-3))]">
                      {group.section}
                    </div>
                  )}
                  <div className="space-y-0.5">
                    {group.items.map((it) => (
                      <NavItem key={it.to} item={it} collapsed={sidebarCollapsed} />
                    ))}
                  </div>
                </div>
              ))}
            </nav>

            <div className="border-t border-white/5 p-2">
              {!sidebarCollapsed ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      className="flex w-full items-center gap-2.5 rounded-md px-2 py-2 hover:bg-[hsl(var(--surface-2))] transition-colors text-left"
                      data-testid="sidebar-user-button"
                    >
                      <div className="flex h-7 w-7 items-center justify-center rounded-md bg-[hsl(var(--primary))/0.15] text-[hsl(var(--primary))] text-xs font-semibold">
                        {(user?.email || 'U').slice(0, 1).toUpperCase()}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-sm font-medium">{user?.full_name || user?.email || 'Operator'}</div>
                        <div className="truncate text-xs text-[hsl(var(--text-3))]">{user?.org_slug ? `@${user.org_slug}` : '—'}</div>
                      </div>
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    <DropdownMenuLabel>{user?.email}</DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => nav('/settings')}>
                      <SettingsIcon className="mr-2 h-4 w-4" /> Settings
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => nav('/api-keys')}>
                      <KeyRound className="mr-2 h-4 w-4" /> API keys
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={handleLogout} data-testid="logout-menu-item">
                      <LogOut className="mr-2 h-4 w-4" /> Sign out
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button onClick={handleLogout} className="flex w-full items-center justify-center rounded-md p-2 hover:bg-[hsl(var(--surface-2))] transition-colors" aria-label="Sign out">
                      <LogOut className="h-4 w-4" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="right">Sign out</TooltipContent>
                </Tooltip>
              )}
            </div>
          </aside>

          {/* Content */}
          <div className={cn('flex min-h-screen w-full flex-col transition-[padding] duration-200', sidebarCollapsed ? 'lg:pl-[72px]' : 'lg:pl-[264px]')}>
            <Topbar />
            <main className="flex-1 px-4 py-6 lg:px-8">
              <Outlet />
            </main>
          </div>
        </div>

        <CommandPalette />
      </div>
    </TooltipProvider>
  );
}

function NavItem({ item, collapsed }) {
  const Icon = item.icon;
  const content = (
    <NavLink
      to={item.to}
      className={({ isActive }) => cn(
        'relative flex items-center gap-2.5 rounded-md px-2.5 py-2 text-sm transition-colors',
        'text-[hsl(var(--text-2))] hover:bg-[hsl(var(--surface-2))] hover:text-[hsl(var(--text-1))]',
        isActive && 'bg-[hsl(var(--surface-2))] text-[hsl(var(--text-1))] before:absolute before:left-0 before:top-1.5 before:bottom-1.5 before:w-[2px] before:rounded-full before:bg-[hsl(var(--primary))]'
      )}
      data-testid={`sidebar-nav-item-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <Icon className="h-4 w-4 shrink-0" />
      {!collapsed && <span className="truncate">{item.label}</span>}
    </NavLink>
  );
  if (collapsed) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>{content}</TooltipTrigger>
        <TooltipContent side="right">{item.label}</TooltipContent>
      </Tooltip>
    );
  }
  return content;
}

function Topbar() {
  const { setCommandOpen } = useUIStore();
  const nav = useNavigate();
  return (
    <header
      className="sticky top-0 z-20 flex h-12 items-center justify-between gap-3 border-b border-white/5 bg-[hsl(var(--surface-1))/0.85] px-4 backdrop-blur supports-[backdrop-filter]:bg-[hsl(var(--surface-1))/0.75] lg:px-8"
      data-testid="app-topbar"
    >
      <div className="flex items-center gap-3 min-w-0">
        <button
          onClick={() => setCommandOpen(true)}
          className="inline-flex items-center gap-2 rounded-md border border-white/10 bg-[hsl(var(--surface-2))] px-2.5 py-1.5 text-xs text-[hsl(var(--text-3))] hover:text-[hsl(var(--text-1))] hover:border-white/20 transition-colors"
          data-testid="command-palette-open-button"
        >
          <Search className="h-3.5 w-3.5" />
          <span className="hidden sm:inline">Search or run command…</span>
          <span className="sm:ml-2 rounded border border-white/10 bg-[hsl(var(--surface-3))] px-1.5 py-0.5 font-mono text-[10px]">⌘K</span>
        </button>
      </div>
      <div className="flex items-center gap-2">
        <Button
          variant="secondary"
          size="sm"
          className="h-8 text-xs"
          onClick={() => nav('/agents?new=1')}
          data-testid="topbar-new-agent-button"
        >
          <PlusCircle className="mr-1.5 h-3.5 w-3.5" />
          New agent
        </Button>
        <button
          onClick={() => nav('/docs')}
          className="hidden md:inline-flex items-center gap-1 rounded-md px-2 py-1.5 text-xs text-[hsl(var(--text-3))] hover:text-[hsl(var(--text-1))]"
          data-testid="topbar-docs-button"
        >
          <Terminal className="h-3.5 w-3.5" /> Docs
        </button>
      </div>
    </header>
  );
}
